// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';

export type IdpInfo =
  | {
      algorithm: 'rs-256';
      scope: string;
      audience: string;
      authority: string;
      clientId: string;
      redirectUri: string;
    }
  | {
      algorithm: 'hs-256-unsafe';
      scope: string;
      audience: string;
      secret: string;
    }
  | {
      // This option exists because we can't rely on "undefined" to represent the absence of an IdP,
      // since the context state starts out as undefined.
      algorithm: 'idp-not-set';
    }
  | undefined;

type IdpContextState = {
  idpInfo: IdpInfo;
  unsetIdpInfo: () => void;
  idpError: string | undefined;
  setIdpError: (errorMsg: string) => void;
  unsetIdpError: () => void;
};

export const IdpContext = React.createContext<IdpContextState | undefined>(undefined);

export const useIdpInfo = () => {
  const context = React.useContext(IdpContext);
  if (context === undefined) {
    throw new Error('useIdpInfo must be used within an IdpProvider');
  }
  return context;
};
